import _ from 'lodash';

import { addTruthyParamsToUrl, stripTrailingSlash } from '@zapier/url-utils';
import {
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
} from '@zapier/toolbox-redux-framework';
import {
  sendFullStoryEvent,
  setFullStoryUserVars,
} from '@zapier/toolbox-tracking';

import Tracking from '../utils/Tracking';
import { getIdentity } from '../utils/IdentityUtils';

export const type = 'abtest';
export const INTERSTATE = 'interstate';
export const LANE = 'lane';
export const NAME = 'name';
export const IS_ELIGIBLE = 'is_eligible';
const CONTROL = 'control';

const identity = getIdentity();

export const api = buildEntityApi(type, path => {
  return addTruthyParamsToUrl(stripTrailingSlash(`/api/v3/abtests/${path}`), {
    identity,
  });
});

export const selectors = buildEntitySelectors(type, sel => {
  sel.interstate = _.partial(sel.dataItem, INTERSTATE);
  sel.lane = _.partial(sel.dataItem, LANE);
  sel.name = _.partial(sel.dataItem, NAME);
  sel.isEligible = _.partial(sel.dataItem, IS_ELIGIBLE);

  sel.hasName = _.partial(sel.matchesSelector, sel.name);

  sel.all.shouldLoad = _.negate(sel.all.hasLoaded);
  sel.all.findByName = _.partial(sel.all.findBy, sel.hasName);
  sel.isControlVariant = test => sel.lane(test) === CONTROL;
  sel.all.isUserEligibleForTest = (testName, state) => {
    const test = sel.all.findByName(testName, state);
    return test ? sel.isEligible(test) : false;
  };

  sel.all.shouldRunVariantForTest = (variant, testName, state) => {
    const test = sel.all.findByName(testName, state);
    if (!test) {
      return false;
    }

    const isEligible = sel.isEligible(test);
    const isVariant = sel.lane(test) === variant;
    const isControlVariant = isVariant && sel.isControlVariant(test);

    // assumption: everyone is eligible for the control variant
    // if not eligible for control variant, should be handled outside the experiment already
    return (isEligible && isVariant) || (!isEligible && isControlVariant);
  };

  // Note: excludes non-eligible tests
  sel.all.testToVariantMap = state =>
    sel.all
      .entities(state)
      .filter(selectors.isEligible)
      .reduce(
        (acc, test) => ({
          ...acc,
          [selectors.name(test)]: selectors.lane(test),
        }),
        {}
      );
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    const overrideLoad = originalActionCreator => {
      return (...args) => (dispatch, getState) => {
        // Whenever abtests load, we want to set FullStory userVars for the
        // abtest entities returned, so we can segment by them.
        return dispatch(originalActionCreator(...args)).then(() => {
          const state = getState();
          setFullStoryUserVars({
            experiments_str: JSON.stringify(
              selectors.all.testToVariantMap(state)
            ),
          });
        });
      };
    };

    entityActions.loadCollection = overrideLoad(entityActions.loadCollection);
    entityActions.forceLoadCollection = overrideLoad(
      entityActions.forceLoadCollection
    );

    entityActions.trackEventIfEligible = ({
      testName,
      eventSuffix,
      selectAdditionalEventData,
      extraEventData,
    }) => {
      return (dispatch, getState) => {
        dispatch(entityActions.loadCollection()).then(() => {
          const state = getState();
          const abtestEntity = selectors.all.findByName(testName, state);
          const lane = selectors.lane(abtestEntity);

          if (!selectors.isEligible(abtestEntity)) {
            return;
          }

          const additionalData = _.isFunction(selectAdditionalEventData)
            ? selectAdditionalEventData(state)
            : {};

          const eventName = `test: ${testName}_${eventSuffix}`;
          const baseData = {
            test: testName,
            variant: lane,
          };
          dispatch(
            Tracking.track(eventName, {
              ...additionalData,
              ...extraEventData,
              ...baseData,
            })
          );
          // FullStory can't handle nested objects, so we only send a subset of
          // context with the event.
          sendFullStoryEvent(eventName, baseData);
        });
      };
    };

    entityActions.trackBirthEventIfEligible = ({
      testName,
      selectAdditionalEventData,
      extraEventData,
    }) =>
      entityActions.trackEventIfEligible({
        testName,
        selectAdditionalEventData,
        extraEventData,
        eventSuffix: 'birth',
      });

    entityActions.trackDeathEventIfEligible = ({
      testName,
      selectAdditionalEventData,
    }) =>
      entityActions.trackEventIfEligible({
        testName,
        selectAdditionalEventData,
        eventSuffix: 'death',
      });
  }
);
